import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { CITY_AREA_SUBURB } from "@/config/constants";

export const LocationSelector = ({
  onLocationChange,
  city = "",
  area = "",
  suburb = "",
  required = false,
  showError = false
}) => {
  const [suburbSearch, setSuburbSearch] = useState("");
  const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
  const [selectedCity, setSelectedCity] = useState(city);
  const [selectedArea, setSelectedArea] = useState(area);
  const [selectedSuburb, setSelectedSuburb] = useState(suburb);

  // Build enriched list: [{suburb, city, area, key}, ...] sorted by suburb name
  const allEntries = useMemo(() => {
    const entries = [];
    const nameCount = {};
    // First pass: count occurrences of each suburb name
    for (const c in CITY_AREA_SUBURB) {
      for (const a in CITY_AREA_SUBURB[c]) {
        for (const s of CITY_AREA_SUBURB[c][a]) {
          nameCount[s] = (nameCount[s] || 0) + 1;
        }
      }
    }
    // Second pass: build entries with disambiguation flag
    for (const c in CITY_AREA_SUBURB) {
      for (const a in CITY_AREA_SUBURB[c]) {
        for (const s of CITY_AREA_SUBURB[c][a]) {
          entries.push({
            suburb: s,
            city: c,
            area: a,
            key: `${c}-${a}-${s}`,
            isDuplicate: nameCount[s] > 1
          });
        }
      }
    }
    return entries.sort((a, b) => a.suburb.localeCompare(b.suburb));
  }, []);

  const filteredEntries = useMemo(() => {
    if (!suburbSearch.trim()) return allEntries;
    const search = suburbSearch.toLowerCase();
    return allEntries.filter(e => e.suburb.toLowerCase().includes(search));
  }, [suburbSearch, allEntries]);

  const handleEntrySelect = (entry) => {
    setSelectedSuburb(entry.suburb);
    setSelectedArea(entry.area);
    setSelectedCity(entry.city);
    setSuburbSearch("");
    setShowSuburbDropdown(false);

    onLocationChange({
      city: entry.city,
      area: entry.area,
      suburb: entry.suburb
    });
  };

  const handleClear = () => {
    setSelectedCity("");
    setSelectedArea("");
    setSelectedSuburb("");
    setSuburbSearch("");
    onLocationChange({ city: "", area: "", suburb: "" });
  };

  return (
    <div className="space-y-3">
      <Label>
        Location {required && <span className="text-red-500">*</span>}
      </Label>

      {/* Suburb Search */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Input
              placeholder="Search suburb..."
              value={suburbSearch || selectedSuburb}
              onChange={(e) => {
                setSuburbSearch(e.target.value);
                setShowSuburbDropdown(true);
              }}
              onFocus={() => setShowSuburbDropdown(true)}
              className={showError && !selectedSuburb ? "border-red-500" : ""}
              data-testid="location-suburb-search"
            />
            {showSuburbDropdown && filteredEntries.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {filteredEntries.slice(0, 50).map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => handleEntrySelect(entry)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                  >
                    {entry.isDuplicate
                      ? <>{entry.suburb} <span className="text-muted-foreground">({entry.city})</span></>
                      : entry.suburb}
                  </button>
                ))}
                {filteredEntries.length > 50 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Type more to narrow results...</p>
                )}
              </div>
            )}
          </div>
          {selectedSuburb && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              data-testid="location-clear-button"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Display selected location hierarchy */}
      {selectedSuburb && selectedCity && (
        <Card className="p-3 bg-blue-50 border border-blue-200">
          <p className="text-sm font-medium text-blue-900">
            {selectedCity} / {selectedArea} / {selectedSuburb}
          </p>
        </Card>
      )}

      {/* Hidden inputs for form submission */}
      <input type="hidden" name="city" value={selectedCity} />
      <input type="hidden" name="area" value={selectedArea} />
      <input type="hidden" name="suburb" value={selectedSuburb} />

      {/* Error message */}
      {showError && !selectedSuburb && (
        <p className="text-xs text-red-500">Location is required</p>
      )}
    </div>
  );
};

export default LocationSelector;
